import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import GurujiChatWidget from '../components/GurujiChatWidget';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Users, Target, Award, FileCheck, ChevronLeft, ChevronRight, Calendar, Clock, ArrowRight, TrendingUp, Sparkles, Star, Quote } from 'lucide-react';
import { stats } from '../mock';

const iconMap = {
  Users, Target, Award, FileCheck
};

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [recentBlogs, setRecentBlogs] = useState([]);
  
  const banners = [
    {
      id: 1,
      image: 'https://customer-assets.emergentagent.com/job_vidya365-coaching/artifacts/wn1dxwcn_lapis-ad-ad-original%20%289%29.png',
      title: 'RBI Grade B',
      subtitle: 'Phase I & II Complete Preparation'
    },
    {
      id: 2,
      image: 'https://customer-assets.emergentagent.com/job_vidya365-coaching/artifacts/jmmlhqp5_lapis-ad-ad-original%20%288%29.png',
      title: 'Banking Exams',
      subtitle: 'Your Gateway to Banking Success'
    },
    {
      id: 3,
      image: 'https://customer-assets.emergentagent.com/job_vidya365-coaching/artifacts/aywx7jig_lapis-ad-48957-original.png',
      title: 'SSC All Exams',
      subtitle: 'Complete Course - CGL, CHSL, MTS, CPO, GD'
    }
  ];

  // Fetch recent blog posts from backend
  useEffect(() => {
    fetchRecentBlogs();
  }, []);

  const fetchRecentBlogs = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/blog`);
      const data = await response.json();
      // Get only the 3 most recent posts
      setRecentBlogs(data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching blogs:', error);
      // Fallback to default data if API fails
      setRecentBlogs([
        {
          id: 1,
          title: 'SSC CGL 2026 Notification Released: 5000+ Vacancies',
          excerpt: 'Staff Selection Commission has released the official notification for SSC CGL 2026 examination.',
          category: 'Job Notifications',
          author_name: 'Vidya365 Team',
          created_at: '2026-03-05',
          read_time: '5 min read',
          featured_image: 'https://images.unsplash.com/photo-1756032433560-56547efed550?w=400&h=250&fit=crop'
        }
      ]);
    }
  };

  // Auto-rotate banners every 4 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % banners.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [banners.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % banners.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + banners.length) % banners.length);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Structured Data for LocalBusiness — comprehensive India-targeting schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": ["EducationalOrganization", "LocalBusiness"],
          "@id": "https://vidya365.in/#organization",
          "name": "Vidya365",
          "alternateName": "Vidya365 Coaching Institute",
          "url": "https://vidya365.in",
          "logo": {
            "@type": "ImageObject",
            "url": "https://customer-assets.emergentagent.com/job_vidya365-coaching/artifacts/051wobd3_IMG-20260204-WA0032.jpg",
            "width": 512,
            "height": 512
          },
          "image": [
            "https://customer-assets.emergentagent.com/job_vidya365-coaching/artifacts/051wobd3_IMG-20260204-WA0032.jpg"
          ],
          "description": "Best coaching institute in Karol Bagh, Delhi for SSC, Banking, IBPS, SBI, RBI Grade B exam preparation. ⭐4.6 Rating · 263+ Reviews · Online & Offline classes · Guruji AI-powered learning.",
          "slogan": "India's No.1 AI-Powered Coaching for Government Exams",
          "foundingDate": "2015",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Unit 8, Apsara Arcade, 7, Pusa Road, near Karol Bagh Metro Station",
            "addressLocality": "Karol Bagh",
            "addressRegion": "Delhi",
            "postalCode": "110005",
            "addressCountry": "IN"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": 28.6519,
            "longitude": 77.1909
          },
          "hasMap": "https://www.google.com/maps/place/Karol+Bagh,+New+Delhi,+Delhi+110005",
          "telephone": "+91-87504-10365",
          "email": "info@vidya365.in",
          "priceRange": "₹₹",
          "currenciesAccepted": "INR",
          "paymentAccepted": "Cash, Credit Card, Debit Card, UPI, Net Banking, Razorpay",
          "openingHoursSpecification": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
              "opens": "08:00",
              "closes": "21:00"
            },
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": "Sunday",
              "opens": "09:00",
              "closes": "18:00"
            }
          ],
          "areaServed": [
            { "@type": "Country", "name": "India" },
            { "@type": "AdministrativeArea", "name": "Delhi NCR" },
            { "@type": "City", "name": "New Delhi" },
            { "@type": "City", "name": "Gurugram" },
            { "@type": "City", "name": "Noida" },
            { "@type": "City", "name": "Faridabad" },
            { "@type": "City", "name": "Ghaziabad" }
          ],
          "knowsLanguage": ["en-IN", "hi-IN"],
          "knowsAbout": [
            "SSC CGL", "SSC CHSL", "SSC MTS", "SSC CPO", "SSC GD",
            "IBPS PO", "IBPS Clerk", "IBPS RRB",
            "SBI PO", "SBI Clerk",
            "RBI Grade B", "NABARD Grade A",
            "Quantitative Aptitude", "Reasoning Ability", "English Language",
            "General Awareness", "Banking Awareness", "Current Affairs"
          ],
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Coaching Programs",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Course",
                  "name": "SSC CGL Coaching",
                  "provider": { "@type": "EducationalOrganization", "name": "Vidya365" }
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Course",
                  "name": "IBPS PO Coaching",
                  "provider": { "@type": "EducationalOrganization", "name": "Vidya365" }
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Course",
                  "name": "RBI Grade B Coaching",
                  "provider": { "@type": "EducationalOrganization", "name": "Vidya365" }
                }
              }
            ]
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.6",
            "reviewCount": "263",
            "bestRating": "5",
            "worstRating": "1"
          },
          "contactPoint": [
            {
              "@type": "ContactPoint",
              "telephone": "+91-87504-10365",
              "contactType": "customer service",
              "areaServed": "IN",
              "availableLanguage": ["en", "hi"],
              "contactOption": "TollFree"
            },
            {
              "@type": "ContactPoint",
              "telephone": "+91-87504-10365",
              "contactType": "sales",
              "areaServed": "IN",
              "availableLanguage": ["en", "hi"]
            }
          ],
          "sameAs": [
            "https://www.facebook.com/vidyaa365/",
            "https://www.instagram.com/vidya365365",
            "https://www.youtube.com/channel/UCLHEDyf9t4VIUp5gatsmhyQ"
          ]
        })}} />

      {/* WebSite schema with SearchAction — unlocks Sitelinks Search Box in Google */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "@id": "https://vidya365.in/#website",
          "url": "https://vidya365.in",
          "name": "Vidya365",
          "description": "India's No.1 AI-Powered Coaching Platform for Government Exams",
          "inLanguage": "en-IN",
          "publisher": { "@id": "https://vidya365.in/#organization" },
          "potentialAction": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": "https://vidya365.in/latest-updates?q={search_term_string}"
            },
            "query-input": "required name=search_term_string"
          }
        })}} />
      
      <Header />

      {/* Marquee/Announcement Bar */}
      <div className="bg-gradient-to-r from-red-600 via-red-500 to-red-600 text-white py-3 overflow-hidden">
        <div className="marquee-container">
          <div className="marquee-content flex items-center space-x-12 animate-marquee whitespace-nowrap">
            <span className="flex items-center">
              <span className="text-yellow-300 mr-2">🤖</span>
              <span className="font-semibold">NEW: Guruji AI - India's No.1 AI Platform for Government Exam Preparation | 24/7 Instant Doubt Solving</span>
            </span>
            <span className="text-yellow-300">•</span>
            <span className="flex items-center">
              <span className="text-yellow-300 mr-2">🎓</span>
              <span className="font-semibold">New Batch Starting Soon - SSC CGL 2026 | Limited Seats Available</span>
            </span>
            <span className="text-yellow-300">•</span>
            <span className="flex items-center">
              <span className="text-yellow-300 mr-2">🏆</span>
              <span className="font-semibold">Many Selections in RBI Grade B 2025 | Join Our Success Story</span>
            </span>
            <span className="text-yellow-300">•</span>
            <span className="flex items-center">
              <span className="text-yellow-300 mr-2">📚</span>
              <span className="font-semibold">Free Demo Classes Available | Call Now: +91 87504 10365</span>
            </span>
            <span className="text-yellow-300">•</span>
            <span className="flex items-center">
              <span className="text-yellow-300 mr-2">🎯</span>
              <span className="font-semibold">IBPS PO 2026 Registration Open | Expert Faculty | Proven Results</span>
            </span>
            <span className="text-yellow-300">•</span>
            <span className="flex items-center">
              <span className="text-yellow-300 mr-2">⭐</span>
              <span className="font-semibold">4.6★ Rated on Google | 263+ Happy Students | Best Coaching in Delhi</span>
            </span>
            {/* Duplicate for seamless loop */}
            <span className="text-yellow-300">•</span>
            <span className="flex items-center">
              <span className="text-yellow-300 mr-2">🤖</span>
              <span className="font-semibold">NEW: Guruji AI - India's No.1 AI Platform for Government Exam Preparation | 24/7 Instant Doubt Solving</span>
            </span>
            <span className="text-yellow-300">•</span>
            <span className="flex items-center">
              <span className="text-yellow-300 mr-2">🎓</span>
              <span className="font-semibold">New Batch Starting Soon - SSC CGL 2026 | Limited Seats Available</span>
            </span>
            <span className="text-yellow-300">•</span>
            <span className="flex items-center">
              <span className="text-yellow-300 mr-2">🏆</span>
              <span className="font-semibold">Many Selections in RBI Grade B 2025 | Join Our Success Story</span>
            </span>
            <span className="text-yellow-300">•</span>
            <span className="flex items-center">
              <span className="text-yellow-300 mr-2">📚</span>
              <span className="font-semibold">Free Demo Classes Available | Call Now: +91 87504 10365</span>
            </span>
            <span className="text-yellow-300">•</span>
            <span className="flex items-center">
              <span className="text-yellow-300 mr-2">🎯</span>
              <span className="font-semibold">IBPS PO 2026 Registration Open | Expert Faculty | Proven Results</span>
            </span>
            <span className="text-yellow-300">•</span>
            <span className="flex items-center">
              <span className="text-yellow-300 mr-2">⭐</span>
              <span className="font-semibold">4.6★ Rated on Google | 263+ Happy Students | Best Coaching in Delhi</span>
            </span>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes marquee {
          0% {
            transform: translateX(0%);
          }
          100% {
            transform: translateX(-50%);
          }
        }

        .animate-marquee {
          animation: marquee 40s linear infinite;
        }

        .marquee-container:hover .animate-marquee {
          animation-play-state: paused;
        }

        .marquee-content {
          display: inline-flex;
        }
      `}</style>

      {/* Hero Banner Carousel */}
      <section className="relative bg-gradient-to-b from-red-50 to-white">
        <div className="relative w-full overflow-hidden">
          {/* Carousel */}
          <div className="relative h-[450px] md:h-[600px]">
            {banners.map((banner, index) => (
              <div
                key={banner.id}
                className={`absolute inset-0 transition-opacity duration-1000 ${
                  index === currentSlide ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <img
                  src={banner.image}
                  alt={banner.title}
                  width="1920"
                  height="600"
                  loading={index === 0 ? "eager" : "lazy"}
                  fetchpriority={index === 0 ? "high" : "auto"}
                  decoding="async"
                  className="w-full h-full object-contain bg-gradient-to-br from-red-600 via-red-500 to-red-700"
                />
              </div>
            ))}
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all z-10"
          >
            <ChevronLeft className="text-red-600" size={28} />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all z-10"
          >
            <ChevronRight className="text-red-600" size={28} />
          </button>

          {/* Dots Indicator */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex space-x-3 z-10">
            {banners.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition-all ${
                  index === currentSlide
                    ? 'bg-red-600 w-8'
                    : 'bg-white/70 hover:bg-white'
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* AI Mock Interview Banner — NEW Feature */}
      <section className="py-8 bg-gradient-to-br from-indigo-900 via-purple-900 to-red-900 text-white relative overflow-hidden" data-testid="home-ai-interview-banner">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 30% 20%, white 1.5px, transparent 1.5px), radial-gradient(circle at 70% 80%, white 1px, transparent 1px)', backgroundSize: '60px 60px, 40px 40px' }} />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-6">
            <div className="flex-1 text-center md:text-left">
              <Badge className="bg-yellow-400 text-gray-900 font-bold mb-3 animate-pulse">⚡ NEW · POWERED BY AI</Badge>
              <h2 className="text-2xl md:text-4xl font-bold mb-2">
                Crack Your <span className="text-yellow-400">Interview</span> with AI
              </h2>
              <p className="text-indigo-100 text-sm md:text-base mb-4 max-w-xl">
                India's first AI Mock Interview platform for <strong>RBI Grade B • SBI PO • IBPS PO • IB ACIO</strong>.
                10 exam-pattern questions, instant scoring, voice or text answers, final personalised report.
              </p>
              <div className="flex flex-wrap justify-center md:justify-start gap-2 mb-4">
                <Badge className="bg-white/15 text-white border-0">🎯 Score per Answer</Badge>
                <Badge className="bg-white/15 text-white border-0">🎙️ Voice + Text</Badge>
                <Badge className="bg-white/15 text-white border-0">📊 Final Report</Badge>
                <Badge className="bg-white/15 text-white border-0">💯 100% Free</Badge>
              </div>
              <Link to="/ai-interview">
                <Button data-testid="home-ai-interview-cta" className="bg-yellow-400 hover:bg-yellow-300 text-gray-900 font-bold px-6 py-5 text-base shadow-xl">
                  Start AI Mock Interview <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
            <div className="hidden md:flex flex-col gap-2 w-72">
              {['RBI Grade B', 'SBI PO', 'IBPS PO', 'IB ACIO'].map((exam, i) => (
                <Link
                  key={exam}
                  to="/ai-interview"
                  className="flex items-center justify-between bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 rounded-lg px-4 py-3 transition-all hover:translate-x-1"
                  data-testid={`home-ai-exam-${i}`}
                >
                  <span className="font-semibold">{exam}</span>
                  <Sparkles size={16} className="text-yellow-400" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white border-y border-gray-200">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => {
              const Icon = iconMap[stat.icon];
              return (
                <div key={stat.label} className="text-center">
                  <div className="flex justify-center mb-3">
                    <div className="bg-red-100 p-4 rounded-full">
                      <Icon className="text-red-600" size={28} />
                    </div>
                  </div>
                  <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-1">{stat.value}</div>
                  <div className="text-gray-600">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Promotional Ad 1: Ghar Baithe Sarkari Naukri */}
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <Link to="/exams" className="block max-w-5xl mx-auto group">
            <div className="relative overflow-hidden rounded-2xl shadow-2xl transition-transform duration-300 group-hover:scale-[1.02]">
              <img
                src="https://customer-assets.emergentagent.com/job_vidya365-coaching/artifacts/qre254ox_lapis-ad-ad-original%20%285%29.png"
                alt="Ghar Baithe Sarkari Naukri Pao - Online Coaching for All Government Exams"
                width="1200"
                height="630"
                loading="lazy"
                decoding="async"
                className="w-full h-auto"
              />
            </div>
          </Link>
        </div>
      </section>

      {/* Featured Video Section */}
      <section
        data-testid="home-featured-video"
        className="py-16 bg-gradient-to-br from-gray-50 via-red-50/30 to-white relative overflow-hidden"
      >
        {/* decorative blobs */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-red-100/40 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-100/40 rounded-full blur-3xl translate-x-1/3 translate-y-1/3 pointer-events-none" />

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 bg-red-100 text-red-700 rounded-full text-sm font-semibold mb-4">
              ▶ Watch Vidya365 in Action
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-3">
              See Why Toppers Choose <span className="text-red-600">Vidya365</span>
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              A quick look at our classes, study material and AI-powered learning that&rsquo;s helped thousands crack SSC, Bank &amp; RBI exams.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {/* Video frame with red accent border + offset shadow */}
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-red-500 via-orange-500 to-red-600 rounded-2xl blur opacity-30 group-hover:opacity-50 transition duration-500" />
              <div className="relative bg-black rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/5">
                <video
                  data-testid="home-video-player"
                  src="https://customer-assets.emergentagent.com/job_vidya365-coaching/artifacts/08uin6w4_1000291474.mp4"
                  controls
                  playsInline
                  preload="none"
                  poster="https://customer-assets.emergentagent.com/job_vidya365-coaching/artifacts/051wobd3_IMG-20260204-WA0032.jpg"
                  className="w-full h-auto block aspect-video bg-black"
                  width="1280"
                  height="720"
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>

            {/* CTA row below the video */}
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/exams">
                <Button
                  data-testid="video-cta-courses"
                  size="lg"
                  className="bg-red-600 hover:bg-red-700 text-white shadow-lg hover:shadow-xl transition-all"
                >
                  Explore Courses <ChevronRight className="ml-1" size={18} />
                </Button>
              </Link>
              <Link to="/student/register">
                <Button
                  data-testid="video-cta-register"
                  size="lg"
                  variant="outline"
                  className="border-red-600 text-red-600 hover:bg-red-50"
                >
                  Start Free Trial
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Guruji AI Highlight Banner - Touchpoint 1 */}
      <section className="bg-gradient-to-r from-purple-700 via-purple-600 to-red-600 py-6 border-y-4 border-yellow-400">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between max-w-6xl mx-auto">
            <div className="flex items-center space-x-4 mb-4 md:mb-0">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center animate-pulse">
                <span className="text-4xl">🙏</span>
              </div>
              <div className="text-white">
                <div className="flex items-center space-x-2 mb-1">
                  <span className="bg-yellow-400 text-purple-900 text-xs font-bold px-3 py-1 rounded-full">NEW</span>
                  <span className="bg-white text-purple-700 text-xs font-bold px-3 py-1 rounded-full">🏆 INDIA'S NO.1</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold">Guruji AI</h2>
                <p className="text-sm md:text-base text-purple-100">India's First AI Platform for Government Exam Preparation</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button className="bg-white text-purple-700 hover:bg-yellow-400 hover:text-purple-900 font-bold px-6 py-3 text-sm shadow-lg transition-all">
                🤖 Try Guruji AI Free
              </Button>
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-purple-700 font-bold px-6 py-3 text-sm">
                📊 See Demo
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Guruji AI Detailed Section - Touchpoint 2 */}
      <section className="py-20 bg-gradient-to-br from-purple-50 via-red-50 to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-purple-200 rounded-full filter blur-3xl opacity-20"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-200 rounded-full filter blur-3xl opacity-20"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-red-600 text-white rounded-full text-sm font-semibold mb-4 shadow-lg">
                <Sparkles size={16} className="text-yellow-300" />
                <span>INDIA'S NO.1 AI PLATFORM</span>
                <Sparkles size={16} className="text-yellow-300" />
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Meet 🙏 <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-red-600">Guruji AI</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
                Your 24/7 personal AI teacher for instant doubt solving and exam preparation
              </p>
              
              {/* Trust Indicators */}
              <div className="flex flex-wrap justify-center gap-6 mb-8">
                <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-md">
                  <span className="text-2xl">🎯</span>
                  <div className="text-left">
                    <div className="text-xs text-gray-500">Powered by</div>
                    <div className="font-bold text-purple-700">OpenAI GPT-4o</div>
                  </div>
                </div>
                <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-md">
                  <span className="text-2xl">⚡</span>
                  <div className="text-left">
                    <div className="text-xs text-gray-500">Response Time</div>
                    <div className="font-bold text-red-600">Instant</div>
                  </div>
                </div>
                <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-md">
                  <span className="text-2xl">🔓</span>
                  <div className="text-left">
                    <div className="text-xs text-gray-500">Access</div>
                    <div className="font-bold text-green-600">Free for All</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-center">
              {/* Left side - Features */}
              <div className="space-y-6">
                <Card className="border-2 border-purple-200 hover:shadow-xl hover:scale-105 transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center mr-4 flex-shrink-0 shadow-lg">
                        <span className="text-2xl">💬</span>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg text-gray-900 mb-2">Instant Doubt Solving</h3>
                        <p className="text-gray-600 text-sm">
                          Get immediate answers to your questions in Quantitative Aptitude, Reasoning, English, General Awareness, and Banking/Economy
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 border-red-200 hover:shadow-xl hover:scale-105 transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center mr-4 flex-shrink-0 shadow-lg">
                        <span className="text-2xl">📚</span>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg text-gray-900 mb-2">Personalized Study Plans</h3>
                        <p className="text-gray-600 text-sm">
                          AI-generated study schedules tailored to your target exam, weak areas, and available preparation time
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 border-green-200 hover:shadow-xl hover:scale-105 transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center mr-4 flex-shrink-0 shadow-lg">
                        <span className="text-2xl">📝</span>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg text-gray-900 mb-2">Practice Question Generator</h3>
                        <p className="text-gray-600 text-sm">
                          Generate unlimited practice MCQs on any topic with instant answers, step-by-step explanations, and difficulty levels
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Right side - CTA */}
              <div className="bg-gradient-to-br from-purple-600 via-purple-700 to-red-600 rounded-2xl p-8 md:p-12 text-white shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-400 rounded-full filter blur-3xl opacity-20"></div>
                <div className="relative z-10">
                  <div className="text-center">
                    <div className="text-7xl mb-6 animate-bounce">🙏</div>
                    <div className="bg-yellow-400 text-purple-900 text-sm font-bold px-4 py-2 rounded-full inline-block mb-4">
                      🏆 INDIA'S #1 AI PLATFORM
                    </div>
                    <h3 className="text-3xl font-bold mb-4">Try Guruji AI Now!</h3>
                    <p className="text-purple-100 mb-8 text-base">
                      Experience the power of AI-assisted learning. Ask any doubt, get instant expert answers. No login required for basic access!
                    </p>
                    
                    <div className="space-y-4">
                      <Link to="/student/login">
                        <Button className="w-full bg-white text-purple-600 hover:bg-yellow-400 hover:text-purple-900 text-lg py-6 font-bold shadow-lg transition-all">
                          🚀 Login to Access Full Features
                        </Button>
                      </Link>
                      <Link to="/student/register">
                        <Button variant="outline" className="w-full border-2 border-white text-white hover:bg-white hover:text-purple-600 text-lg py-6 font-bold">
                          📝 Register Free Account
                        </Button>
                      </Link>
                    </div>

                    <div className="mt-8 pt-6 border-t border-purple-400">
                      <p className="text-xs text-purple-100 mb-3">
                        ✨ Powered by <strong>OpenAI GPT-4o</strong> • Available <strong>24/7</strong> • Trusted by <strong>5,000+ Students</strong>
                      </p>
                      <div className="flex justify-center items-center space-x-2">
                        <Star className="text-yellow-300 fill-yellow-300" size={16} />
                        <Star className="text-yellow-300 fill-yellow-300" size={16} />
                        <Star className="text-yellow-300 fill-yellow-300" size={16} />
                        <Star className="text-yellow-300 fill-yellow-300" size={16} />
                        <Star className="text-yellow-300 fill-yellow-300" size={16} />
                        <span className="text-xs text-white ml-2">Rated 5/5 by Students</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Promotional Ad 2: Bank PO Selection */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4">
          <Link to="/exams" className="block max-w-5xl mx-auto group">
            <div className="relative overflow-hidden rounded-2xl shadow-2xl transition-transform duration-300 group-hover:scale-[1.02]">
              <img
                src="https://customer-assets.emergentagent.com/job_vidya365-coaching/artifacts/ghner3nz_lapis-ad-ad-original.png"
                alt="Bank PO Selection - Your Turn is Now! Focused Online Coaching for IBPS, SBI, Clerk"
                width="1200"
                height="630"
                loading="lazy"
                decoding="async"
                className="w-full h-auto"
              />
            </div>
          </Link>
        </div>
      </section>

      {/* Quick Overview Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Your Path to Success Starts Here
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Comprehensive coaching programs designed to help you achieve your government job dreams
              </p>
            </div>

            {/* SEO-Optimized Exam Grid with Keywords */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {/* SSC Courses */}
              <div className="bg-white p-6 rounded-lg shadow-md border-2 border-gray-100 hover:border-red-200 transition-all">
                <h3 className="text-xl font-bold text-gray-900 mb-3">SSC Coaching</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">✓</span>
                    <span className="text-sm">SSC CGL Exam Coaching</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">✓</span>
                    <span className="text-sm">SSC CHSL Exam Coaching</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">✓</span>
                    <span className="text-sm">SSC CPO Exam Coaching</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">✓</span>
                    <span className="text-sm">SSC MTS Exam Coaching</span>
                  </li>
                </ul>
              </div>

              {/* IBPS Courses */}
              <div className="bg-white p-6 rounded-lg shadow-md border-2 border-gray-100 hover:border-red-200 transition-all">
                <h3 className="text-xl font-bold text-gray-900 mb-3">IBPS Coaching</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">✓</span>
                    <span className="text-sm">IBPS PO Exam Coaching</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">✓</span>
                    <span className="text-sm">IBPS Clerk Exam Coaching</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">✓</span>
                    <span className="text-sm">IBPS RRB Exam Coaching</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">✓</span>
                    <span className="text-sm">IBPS SO Coaching</span>
                  </li>
                </ul>
              </div>

              {/* SBI Courses */}
              <div className="bg-white p-6 rounded-lg shadow-md border-2 border-gray-100 hover:border-red-200 transition-all">
                <h3 className="text-xl font-bold text-gray-900 mb-3">SBI Coaching</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">✓</span>
                    <span className="text-sm">SBI PO Exam Coaching</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">✓</span>
                    <span className="text-sm">SBI Clerk Exam Coaching</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">✓</span>
                    <span className="text-sm">SBI SO Coaching</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">✓</span>
                    <span className="text-sm">SBI Apprentice</span>
                  </li>
                </ul>
              </div>

              {/* RBI & Others */}
              <div className="bg-white p-6 rounded-lg shadow-md border-2 border-gray-100 hover:border-red-200 transition-all">
                <h3 className="text-xl font-bold text-gray-900 mb-3">RBI & Regulatory</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">✓</span>
                    <span className="text-sm">RBI Grade B Coaching</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">✓</span>
                    <span className="text-sm">NABARD Grade A/B</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">✓</span>
                    <span className="text-sm">SEBI Grade A</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">✓</span>
                    <span className="text-sm">RBI Assistant</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Location-Specific SEO Content */}
            <div className="bg-gradient-to-br from-red-50 to-white rounded-2xl p-8 md:p-12 border-2 border-red-100 mb-12">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  Best SSC & Bank Coaching in Delhi
                </h2>
                <p className="text-lg text-gray-700 leading-relaxed max-w-4xl mx-auto">
                  Welcome to <strong>Vidya365</strong> - Your trusted partner for <strong>SSC coaching</strong> and <strong>Bank coaching in Delhi</strong>. 
                  We are recognized as one of the <strong>best coaching institutes in Delhi</strong> for 
                  government exam preparation.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="text-red-600 mr-2">📍</span>
                    Why Choose Vidya365?
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2 mt-1">✓</span>
                      <span><strong>4.6★ Rating</strong> with 263+ genuine student reviews on Google</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2 mt-1">✓</span>
                      <span>Easily accessible location in <strong>New Delhi</strong></span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2 mt-1">✓</span>
                      <span>Expert faculty with 10+ years of experience</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2 mt-1">✓</span>
                      <span>Proven track record of selections in SSC, IBPS, SBI, RBI</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2 mt-1">✓</span>
                      <span>Both <strong>Online and Offline classes</strong> available</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="text-red-600 mr-2">🎯</span>
                    Our Specializations
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2 mt-1">✓</span>
                      <span><strong>Best RBI Grade B Coaching in Delhi</strong> - Phase 1 & 2</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2 mt-1">✓</span>
                      <span><strong>Best Bank Coaching in Delhi</strong> - IBPS PO, SBI PO</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2 mt-1">✓</span>
                      <span><strong>Top SSC Coaching in Delhi</strong> - CGL, CHSL, CPO, MTS</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2 mt-1">✓</span>
                      <span>NABARD & SEBI coaching with specialized faculty</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2 mt-1">✓</span>
                      <span>AI-powered personalized learning with <strong>Guruji AI</strong></span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-8 text-center">
                <p className="text-gray-600 mb-4">
                  📞 Call us: <a href="tel:+918750410365" className="text-red-600 font-semibold hover:underline">+91 87504 10365</a>
                  {" | "}
                  📍 Unit 8, Apsara Arcade, Pusa Road, New Delhi - 110005
                </p>
                <div className="flex flex-wrap gap-4 justify-center">
                  <Link to="/exams">
                    <Button className="bg-red-600 hover:bg-red-700 text-white px-6 py-3">
                      View All Courses
                    </Button>
                  </Link>
                  <Link to="/start-preparation">
                    <Button variant="outline" className="border-2 border-red-600 text-red-600 hover:bg-red-50 px-6 py-3">
                      Visit Our Center
                    </Button>
                  </Link>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <Link to="/exams" className="group">
                <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-all border-2 border-transparent hover:border-red-200 overflow-hidden">
                  <div className="mb-4 overflow-hidden rounded-lg">
                    <img 
                      src="https://images.pexels.com/photos/13970482/pexels-photo-13970482.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" 
                      alt="Banking Institution India"
                      width="940"
                      height="192"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-48 object-cover rounded-lg group-hover:scale-105 transition-transform"
                    />
                  </div>
                  <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mb-4 group-hover:bg-red-600 transition-colors">
                    <Award className="text-red-600 group-hover:text-white" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">12+ Courses</h3>
                  <p className="text-gray-600 mb-4">
                    Complete preparation for SSC, Bank, and RBI exams with expert faculty
                  </p>
                  <span className="text-red-600 font-medium group-hover:underline">
                    View All Courses →
                  </span>
                </div>
              </Link>

              <Link to="/student-benefits" className="group">
                <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-all border-2 border-transparent hover:border-red-200 overflow-hidden">
                  <div className="mb-4 overflow-hidden rounded-lg">
                    <img 
                      src="https://images.unsplash.com/photo-1569653402334-2e98fbaa80ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjBnb3Zlcm5tZW50JTIwZXhhbSUyMGhhbGwlMjBleGFtaW5hdGlvbnxlbnwwfHx8fDE3NzI2OTQ1OTN8MA&ixlib=rb-4.1.0&q=85" 
                      alt="Exam Preparation"
                      width="940"
                      height="192"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-48 object-cover rounded-lg group-hover:scale-105 transition-transform"
                    />
                  </div>
                  <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mb-4 group-hover:bg-red-600 transition-colors">
                    <Target className="text-red-600 group-hover:text-white" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Flexible Learning</h3>
                  <p className="text-gray-600 mb-4">
                    Choose from offline classes, online courses, or comprehensive test series
                  </p>
                  <span className="text-red-600 font-medium group-hover:underline">
                    Explore Learning Modes →
                  </span>
                </div>
              </Link>

              <Link to="/about" className="group">
                <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-all border-2 border-transparent hover:border-red-200 overflow-hidden">
                  <div className="mb-4 overflow-hidden rounded-lg">
                    <img 
                      src="https://images.unsplash.com/photo-1688701035915-ad0399f05667?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHw0fHxpbmRpYW4lMjBnb3Zlcm5tZW50JTIwZXhhbSUyMGhhbGwlMjBleGFtaW5hdGlvbnxlbnwwfHx8fDE3NzI2OTQ1OTN8MA&ixlib=rb-4.1.0&q=85" 
                      alt="Government Institution"
                      width="940"
                      height="192"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-48 object-cover rounded-lg group-hover:scale-105 transition-transform"
                    />
                  </div>
                  <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mb-4 group-hover:bg-red-600 transition-colors">
                    <Users className="text-red-600 group-hover:text-white" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Expert Faculty</h3>
                  <p className="text-gray-600 mb-4">
                    Learn from 50+ experienced educators with proven track records
                  </p>
                  <span className="text-red-600 font-medium group-hover:underline">
                    Learn More About Us →
                  </span>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Promotional Ad 3: SSC CGL - Sarkari Naukri Pakki */}
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <Link to="/exams" className="block max-w-5xl mx-auto group">
            <div className="relative overflow-hidden rounded-2xl shadow-2xl transition-transform duration-300 group-hover:scale-[1.02]">
              <img
                src="https://customer-assets.emergentagent.com/job_vidya365-coaching/artifacts/68sxj3dq_lapis-ad-ad-original%20%281%29.png"
                alt="Sarkari Naukri Pakki - Abhi Start Karo! SSC CGL Test Series + Live Classes"
                width="1200"
                height="630"
                loading="lazy"
                decoding="async"
                className="w-full h-auto"
              />
            </div>
          </Link>
        </div>
      </section>

      {/* Blog Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 flex items-center justify-center">
                <TrendingUp className="mr-3 text-red-600" size={32} />
                Latest Updates & News
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Stay updated with exam notifications, results, and preparation tips
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-8">
              {recentBlogs.map((post) => (
                <Card key={post.id} className="overflow-hidden hover:shadow-xl transition-shadow border-2 hover:border-red-200 group">
                  <div className="overflow-hidden">
                    <img 
                      src={post.featured_image || 'https://images.unsplash.com/photo-1756032433560-56547efed550?w=400&h=250&fit=crop'} 
                      alt={post.title}
                      width="400"
                      height="192"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform"
                    />
                  </div>
                  <CardHeader>
                    <Badge className="w-fit bg-red-600 text-white mb-2">
                      {post.category}
                    </Badge>
                    <CardTitle className="text-lg hover:text-red-600 transition-colors line-clamp-2">
                      <Link to={`/latest-updates/${post.id}`}>{post.title}</Link>
                    </CardTitle>
                    <CardDescription className="text-sm mt-2 line-clamp-2">
                      {post.excerpt}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                      <span className="flex items-center">
                        <Calendar size={12} className="mr-1" />
                        {new Date(post.created_at).toLocaleDateString()}
                      </span>
                      <span className="flex items-center">
                        <Clock size={12} className="mr-1" />
                        {post.read_time}
                      </span>
                    </div>
                    <Link to={`/latest-updates/${post.id}`}>
                      <button className="text-red-600 font-medium flex items-center hover:underline text-sm">
                        Read More <ArrowRight size={14} className="ml-1" />
                      </button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center">
              <Link to="/latest-updates">
                <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-3">
                  View All Articles
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Promotional Ad 4: Police Exam - Uniform Pehno Desh Bachao */}
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <Link to="/exams" className="block max-w-5xl mx-auto group">
            <div className="relative overflow-hidden rounded-2xl shadow-2xl transition-transform duration-300 group-hover:scale-[1.02]">
              <img
                src="https://customer-assets.emergentagent.com/job_vidya365-coaching/artifacts/70e8nww5_lapis-ad-ad-original%20%282%29.png"
                alt="Uniform Pehno Desh Bachao - Delhi/UP Police Exam Preparation"
                width="1200"
                height="630"
                loading="lazy"
                decoding="async"
                className="w-full h-auto"
              />
            </div>
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-red-600 to-red-700 rounded-2xl p-8 md:p-12 text-white text-center shadow-xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Start Your Preparation?
            </h2>
            <p className="text-xl mb-8 text-red-50">
              Join thousands of successful students who achieved their dreams with Vidya365
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/start-preparation">
                <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 text-lg px-8 py-6">
                  Enroll Now
                </Button>
              </Link>
              <Link to="/exams">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-red-600 text-lg px-8 py-6">
                  View Courses
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                What Our Students Say
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
                Trusted by hundreds of successful students
              </p>
              
              {/* Google Rating Badge */}
              <div className="inline-flex items-center bg-white rounded-full px-6 py-3 shadow-lg border-2 border-gray-200">
                <div className="flex items-center mr-4">
                  <span className="text-4xl font-bold text-gray-900 mr-2">4.6</span>
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star 
                        key={star} 
                        className={`${star <= 4 ? 'text-yellow-400 fill-yellow-400' : 'text-yellow-400 fill-yellow-400'} w-5 h-5`}
                        size={20}
                      />
                    ))}
                  </div>
                </div>
                <div className="text-left border-l-2 border-gray-200 pl-4">
                  <div className="text-sm font-semibold text-gray-900">Google Rating</div>
                  <div className="text-xs text-gray-600">Based on 263 reviews</div>
                </div>
              </div>
            </div>

            {/* Testimonials Grid */}
            <div className="grid md:grid-cols-3 gap-6">
              {/* Review 1 */}
              <Card className="relative overflow-hidden hover:shadow-xl transition-shadow border-2 hover:border-red-200 bg-white">
                <CardContent className="p-6">
                  <div className="absolute top-4 right-4 opacity-10">
                    <Quote size={48} className="text-red-600" />
                  </div>
                  <div className="flex mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star key={star} className="text-yellow-400 fill-yellow-400 w-4 h-4" size={16} />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    "Great place for coaching with experienced faculty. The test series helped me improve my weak areas significantly. Highly recommended for SSC and Bank exam preparation."
                  </p>
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                      <span className="text-red-600 font-bold text-sm">SJ</span>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Sarthak Jain</div>
                      <div className="text-xs text-gray-500">SSC CGL Student</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Review 2 */}
              <Card className="relative overflow-hidden hover:shadow-xl transition-shadow border-2 hover:border-red-200 bg-white">
                <CardContent className="p-6">
                  <div className="absolute top-4 right-4 opacity-10">
                    <Quote size={48} className="text-red-600" />
                  </div>
                  <div className="flex mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star key={star} className="text-yellow-400 fill-yellow-400 w-4 h-4" size={16} />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    "The content quality is reliable and up-to-date with latest exam patterns. Teachers are very supportive and the doubt clearing sessions are extremely helpful."
                  </p>
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                      <span className="text-red-600 font-bold text-sm">RS</span>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Rudra Singh</div>
                      <div className="text-xs text-gray-500">Bank PO Aspirant</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Review 3 */}
              <Card className="relative overflow-hidden hover:shadow-xl transition-shadow border-2 hover:border-red-200 bg-white">
                <CardContent className="p-6">
                  <div className="absolute top-4 right-4 opacity-10">
                    <Quote size={48} className="text-red-600" />
                  </div>
                  <div className="flex mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star key={star} className="text-yellow-400 fill-yellow-400 w-4 h-4" size={16} />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    "The atmosphere is positive and motivating. Mock tests are very similar to actual exams which boosted my confidence. Successfully cleared my exam thanks to Vidya365!"
                  </p>
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                      <span className="text-red-600 font-bold text-sm">AJ</span>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Aman Juneja</div>
                      <div className="text-xs text-gray-500">RBI Grade B Student</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Google Link */}
            <div className="text-center mt-8">
              <a 
                href="https://share.google/DHPzG6hMYglGyCKLQ" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center text-red-600 hover:text-red-700 font-medium text-sm hover:underline"
              >
                <span className="mr-2">View all reviews on Google</span>
                <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Promotional Ad 5: Bank PO Selection (Second placement) */}
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <Link to="/exams" className="block max-w-5xl mx-auto group">
            <div className="relative overflow-hidden rounded-2xl shadow-2xl transition-transform duration-300 group-hover:scale-[1.02]">
              <img
                src="https://customer-assets.emergentagent.com/job_vidya365-coaching/artifacts/l9y97n8n_lapis-ad-ad-original%20%283%29.png"
                alt="Bank PO Selection - Your Turn is Now! Join Free Demo"
                width="1200"
                height="630"
                loading="lazy"
                decoding="async"
                className="w-full h-auto"
              />
            </div>
          </Link>
        </div>
      </section>

      {/* Staff Login Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl shadow-2xl overflow-hidden">
              <div className="grid md:grid-cols-2">
                {/* Left Side - Info */}
                <div className="p-10 text-white">
                  <h2 className="text-3xl font-bold mb-4">Staff & Admin Portal</h2>
                  <p className="text-gray-300 mb-6">
                    Access the admin dashboard to manage tests, students, blog posts, study materials, and more.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                      Manage Tests & Questions
                    </li>
                    <li className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                      View Student Performance
                    </li>
                    <li className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                      Publish Blog Posts & Updates
                    </li>
                    <li className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                      Upload Study Materials
                    </li>
                  </ul>
                </div>

                {/* Right Side - Login Card */}
                <div className="bg-white p-10 flex items-center justify-center">
                  <div className="w-full">
                    <div className="text-center mb-6">
                      <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                        </svg>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-800 mb-2">Admin Login</h3>
                      <p className="text-gray-600 text-sm">For staff and administrators only</p>
                    </div>
                    
                    <Link to="/admin/login">
                      <button className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-4 px-6 rounded-lg text-lg transition-colors shadow-lg">
                        Access Admin Panel →
                      </button>
                    </Link>

                    <p className="text-xs text-gray-500 text-center mt-4">
                      Authorized personnel only. All activities are logged.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* Floating Guruji AI Chat Widget */}
      <GurujiChatWidget />
    </div>
  );
};

export default Home;
